import React from 'react';
import { Box, Chip, InputAdornment, InputBase, Typography, AutocompleteRenderInputParams, AutocompleteRenderOptionState } from '@mui/material';
import { makeStyles, withStyles } from 'tss-react/mui';
import { AsyncAutocompleteRaw, AutocompleteHighlighter } from '../../../Inputs';
import { Search } from '@mui/icons-material';
import { usePatientsSearch } from './PatientsSearch.hook';


export const StyledClientsSearchInputBase = withStyles(InputBase, (theme, params) => ({
  root: {
    backgroundColor: theme.palette.grey[100],
    display: 'flex',
    paddingLeft: 8,
    paddingTop: 3,
    paddingBottom: 3,
    borderRadius: 4,
    boxShadow: `0px 0px 0px 0px ${theme.palette[params.color ?? 'secondary'].main} inset`,
    transition: 'box-shadow 100ms',
  },
  focused: {
    transition: 'box-shadow 50ms',
    boxShadow: `0px 0px 0px 2px ${theme.palette[params.color ?? 'secondary'].main} inset`
  },
}));


const useStyles = makeStyles<Partial<PatientsSearchProps>>()({
  listbox: {
    maxHeight: '220px',
  },
});


export type UserSearch = {
  id: string;
  firstName: string;
  lastName: string;
  preferredName: string;
  active: boolean;
};


const NameHighlighter = withStyles(AutocompleteHighlighter, theme => ({
  highlight: {
    fontWeight: 600,
    color: theme.palette.secondary.main
  }
}));


export const patientsAutocompleteRenderItem = (option: UserSearch, state: Pick<AutocompleteRenderOptionState, 'inputValue' | 'selected'>) => (
  <Box display="flex" justifyContent="space-between" alignItems="center" width="100%" flex="1">
    <Typography>
      <NameHighlighter
        text={`${option.firstName}${option.preferredName ? ' (' + option.preferredName + ')' : ''} ${option.lastName}`}
        highlight={state.inputValue}
      />
    </Typography>
    {!option.active && <Chip label="Inactive" color="default" size="small" />}
  </Box>
);


const CustomAutocompleteInput = (params: AutocompleteRenderInputParams) => {
  const { ref: inputRef, ...inputProps } = params.inputProps as any;

  return (
    <div ref={params.InputProps.ref}>
      <StyledClientsSearchInputBase
        {...inputProps}
        endAdornment={params.InputProps.endAdornment}
        inputRef={inputRef}
        fullWidth
        placeholder="Search clients"
        startAdornment={<InputAdornment position="start"><Search color="disabled" /></InputAdornment>}
      />
    </div>
  );
};


export type PatientsSearchProps = {
  onPatientSelect: (patient: UserSearch) => void;
  customAutocompleteInput?: (params: AutocompleteRenderInputParams) => React.ReactElement;
};


export const PatientsSearch: React.FC<PatientsSearchProps> = (props) => {
  const { classes } = useStyles(props, { props });
  const searchPatients = usePatientsSearch();
  const renderInput = props.customAutocompleteInput || CustomAutocompleteInput;

  return (
    <AsyncAutocompleteRaw
      forcePopupIcon={false}
      fullWidth
      blurOnSelect
      clearOnBlur
      disablePortal
      openWithNoTerm={false}
      noOptionsText="No results"
      name="patients-search"
      classes={classes}
      renderInput={renderInput}
      renderOption={patientsAutocompleteRenderItem}
      setValue={(patient: UserSearch) => {
        props.onPatientSelect(patient);
      }}
      asyncOptions={async (term) => await searchPatients(term)}
    />
  );
};
